import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  AccessTime as AccessTimeIcon,
  Security as SecurityIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { tokenStatusAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';

const TokenStatusDialog = ({ open, onClose }) => {
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [tokenStatuses, setTokenStatuses] = useState([]);
  const [expiringTokens, setExpiringTokens] = useState([]);
  const [lastChecked, setLastChecked] = useState(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null,
    loading: false,
  });

  const checkAllTokens = async () => {
    setLoading(true);
    try {
      console.log('Checking token status...');
      console.log(
        'Auth token:',
        localStorage.getItem('access_token') ? 'Present' : 'Missing'
      );

      const [statusResponse, expiringResponse] = await Promise.all([
        tokenStatusAPI.checkAllTokens(),
        tokenStatusAPI.getExpiringTokens(24),
      ]);

      console.log('Token status response:', statusResponse);
      console.log('Expiring tokens response:', expiringResponse);

      setTokenStatuses(statusResponse.token_statuses || []);
      setExpiringTokens(expiringResponse.expiring_tokens || []);
      setLastChecked(new Date().toLocaleString());

      showSuccess('Token status updated successfully');
    } catch (error) {
      console.error('Error checking tokens:', error);
      console.error('Error details:', error.response?.data);
      showError(
        'Failed to check token status: ' +
          (error.response?.data?.detail || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      checkAllTokens();
    }
  }, [open]);

  const handleRemoveAllInvalid = () => {
    const invalidCount = tokenStatuses.filter((token) => !token.valid).length;
    setConfirmDialog({
      open: true,
      title: 'Remove All Invalid Tokens',
      message: `Are you sure you want to remove all ${invalidCount} invalid tokens? This action cannot be undone.`,
      action: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          const result = await tokenStatusAPI.removeAllInvalidTokens();
          showSuccess(result.message);
          await checkAllTokens(); // Refresh the list
          setConfirmDialog({
            open: false,
            title: '',
            message: '',
            action: null,
            loading: false,
          });
        } catch (error) {
          console.error('Error removing invalid tokens:', error);
          showError(
            'Failed to remove invalid tokens: ' +
              (error.response?.data?.detail || error.message)
          );
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  const handleRemoveSingle = (accountId, accountName, platform) => {
    setConfirmDialog({
      open: true,
      title: 'Remove Token',
      message: `Are you sure you want to remove the ${platform} account "${accountName}"? This action cannot be undone.`,
      action: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          const result = await tokenStatusAPI.removeSingleToken(accountId);
          showSuccess(result.message);
          await checkAllTokens(); // Refresh the list
          setConfirmDialog({
            open: false,
            title: '',
            message: '',
            action: null,
            loading: false,
          });
        } catch (error) {
          console.error('Error removing token:', error);
          showError(
            'Failed to remove token: ' +
              (error.response?.data?.detail || error.message)
          );
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  const handleConfirmAction = () => {
    if (confirmDialog.action) {
      confirmDialog.action();
    }
  };

  const handleCancelAction = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      action: null,
      loading: false,
    });
  };

  const getStatusIcon = (valid, expiresSoon) => {
    if (!valid) return <ErrorIcon color="error" />;
    if (expiresSoon) return <WarningIcon color="warning" />;
    return <CheckCircleIcon color="success" />;
  };

  const getStatusColor = (valid, expiresSoon) => {
    if (!valid) return 'error';
    if (expiresSoon) return 'warning';
    return 'success';
  };

  const getStatusText = (valid, expiresSoon) => {
    if (!valid) return 'Invalid/Expired';
    if (expiresSoon) return 'Expires Soon';
    return 'Valid';
  };

  const formatExpirationTime = (expiresInSeconds) => {
    if (!expiresInSeconds) return 'Unknown';

    const hours = Math.floor(expiresInSeconds / 3600);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} remaining`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
    } else {
      const minutes = Math.floor(expiresInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
    }
  };

  const invalidTokens = tokenStatuses.filter((t) => !t.valid);
  const expiringSoonTokens = tokenStatuses.filter(
    (t) => t.valid && t.expires_soon
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #161b22 0%, #21262d 100%)',
            border: '1px solid rgba(48, 54, 61, 0.5)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: '#f0f6fc',
            borderBottom: '1px solid rgba(48, 54, 61, 0.5)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Token Status Monitor
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#8b949e' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Card
              sx={{
                flex: 1,
                background: 'linear-gradient(135deg, #21262d 0%, #30363d 100%)',
                border: '1px solid rgba(48, 54, 61, 0.5)',
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography
                  variant="h4"
                  sx={{ color: '#2ea043', fontWeight: 700 }}
                >
                  {tokenStatuses.filter((t) => t.valid).length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  Valid Tokens
                </Typography>
              </CardContent>
            </Card>

            <Card
              sx={{
                flex: 1,
                background: 'linear-gradient(135deg, #21262d 0%, #30363d 100%)',
                border: '1px solid rgba(48, 54, 61, 0.5)',
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography
                  variant="h4"
                  sx={{ color: '#f78166', fontWeight: 700 }}
                >
                  {invalidTokens.length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  Invalid Tokens
                </Typography>
              </CardContent>
            </Card>

            <Card
              sx={{
                flex: 1,
                background: 'linear-gradient(135deg, #21262d 0%, #30363d 100%)',
                border: '1px solid rgba(48, 54, 61, 0.5)',
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography
                  variant="h4"
                  sx={{ color: '#d29922', fontWeight: 700 }}
                >
                  {expiringSoonTokens.length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  Expiring Soon
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Alerts */}
          {invalidTokens.length > 0 && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<DeleteForeverIcon />}
                  onClick={handleRemoveAllInvalid}
                  disabled={loading}
                  sx={{ color: 'white' }}
                >
                  Remove All Invalid
                </Button>
              }
            >
              <AlertTitle>Invalid Tokens Detected</AlertTitle>
              {invalidTokens.length} token(s) are invalid or expired and need to
              be reconnected.
            </Alert>
          )}

          {expiringSoonTokens.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Tokens Expiring Soon</AlertTitle>
              {expiringSoonTokens.length} token(s) will expire within 24 hours.
            </Alert>
          )}

          {/* Token List */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: '#f0f6fc', fontWeight: 600 }}
              >
                Platform Tokens
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  loading ? <CircularProgress size={16} /> : <RefreshIcon />
                }
                onClick={checkAllTokens}
                disabled={loading}
                sx={{
                  borderColor: '#30363d',
                  color: '#8b949e',
                  '&:hover': {
                    borderColor: '#58a6ff',
                    color: '#58a6ff',
                  },
                }}
              >
                Refresh
              </Button>
            </Box>

            {tokenStatuses.length === 0 ? (
              <Box
                sx={{
                  bgcolor: 'rgba(22, 27, 34, 0.5)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  border: '1px dashed rgba(48, 54, 61, 0.5)',
                }}
              >
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  {loading
                    ? 'Checking tokens...'
                    : 'No platform tokens found. Connect your social media accounts first.'}
                </Typography>
              </Box>
            ) : (
              <List
                sx={{ bgcolor: 'rgba(22, 27, 34, 0.5)', borderRadius: 2, p: 1 }}
              >
                {tokenStatuses.map((token, index) => (
                  <React.Fragment key={token.account_id}>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon>
                        {getStatusIcon(token.valid, token.expires_soon)}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ color: '#f0f6fc', fontWeight: 500 }}
                            >
                              {token.platform.charAt(0).toUpperCase() +
                                token.platform.slice(1)}
                            </Typography>
                            <Chip
                              label={getStatusText(
                                token.valid,
                                token.expires_soon
                              )}
                              size="small"
                              color={getStatusColor(
                                token.valid,
                                token.expires_soon
                              )}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ color: '#8b949e' }}
                            >
                              {token.account_name} •{' '}
                              {token.client_name || 'Unknown Client'}
                            </Typography>
                            {token.expires_in_seconds && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mt: 0.5,
                                }}
                              >
                                <AccessTimeIcon
                                  sx={{ fontSize: 14, color: '#8b949e' }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#8b949e' }}
                                >
                                  {formatExpirationTime(
                                    token.expires_in_seconds
                                  )}
                                </Typography>
                              </Box>
                            )}
                            {token.error && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#f78166',
                                  mt: 0.5,
                                  display: 'block',
                                }}
                              >
                                Error: {token.error}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      {!token.valid && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() =>
                              handleRemoveSingle(
                                token.account_id,
                                token.account_name,
                                token.platform
                              )
                            }
                            disabled={loading}
                            sx={{ color: '#f78166' }}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                    {index < tokenStatuses.length - 1 && (
                      <Divider sx={{ bgcolor: 'rgba(48, 54, 61, 0.5)' }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {lastChecked && (
            <Typography
              variant="caption"
              sx={{ color: '#8b949e', fontStyle: 'italic' }}
            >
              Last checked: {lastChecked}
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{ p: 3, borderTop: '1px solid rgba(48, 54, 61, 0.5)' }}
        >
          <Button
            onClick={onClose}
            sx={{
              color: '#8b949e',
              '&:hover': {
                color: '#f0f6fc',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCancelAction}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{ color: '#f0f6fc', borderBottom: '1px solid #30363d' }}
        >
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ color: '#8b949e', mb: 2 }}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #30363d' }}>
          <Button
            onClick={handleCancelAction}
            disabled={confirmDialog.loading}
            sx={{
              color: '#8b949e',
              '&:hover': {
                color: '#f0f6fc',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            disabled={confirmDialog.loading}
            variant="contained"
            color="error"
            startIcon={
              confirmDialog.loading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{
              bgcolor: '#da3633',
              '&:hover': {
                bgcolor: '#b02a28',
              },
            }}
          >
            {confirmDialog.loading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TokenStatusDialog;
